/**
 * TanStack Query hooks for the admin console. All admin data is
 * read-mostly so we lean on `useQuery` everywhere with a 30s
 * `refetchInterval` for tiles that the operator watches actively
 * (overview, queue depths, health).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bulkAdminUserAction,
  changeAdminUserRole,
  deleteAdminClassroom,
  deleteAdminUser,
  actionReport,
  adminGlobalSearch,
  createFeatureFlag,
  dismissReport,
  forceApproveSubmission,
  handleAdminMessage,
  listAdminMessages,
  forceRejectSubmission,
  getAdminConfig,
  getAdminHealth,
  getAdminOverview,
  getAdminUser,
  getEmailOutbox,
  listFeatureFlags,
  sendDigests,
  toggleFeatureFlag,
  listAdminAuditLog,
  listAdminClassrooms,
  listAdminPublishedApps,
  listAdminReports,
  listAdminSubmissions,
  listAdminUsers,
  listModerationAudit,
  takedownPublishedApp,
  pauseAdminClassroom,
  setAdminClassroomAllowPublish,
  suspendAdminUser,
  unpauseAdminClassroom,
  unsuspendAdminUser,
  withdrawSubmission,
  type AdminAuditFilters,
  type AdminUserRole,
  type BulkUserAction,
  type InboundStatus,
  type ListClassroomsParams,
  type ListMessagesParams,
  type ListPublishedAppsParams,
  type ListReportsParams,
  type ListSubmissionsParams,
  type ListUsersParams,
  type ModerationAuditFilters,
} from './adminApi';

export function useAdminOverview() {
  return useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: getAdminOverview,
    refetchInterval: 30_000,
  });
}

export function useAdminUsers(params: ListUsersParams) {
  return useQuery({
    queryKey: ['admin', 'users', params],
    queryFn: () => listAdminUsers(params),
    placeholderData: (prev) => prev,
  });
}

export function useAdminUser(id: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'users', 'detail', id],
    queryFn: () => getAdminUser(id as string),
    enabled: !!id,
  });
}

function invalidateUsers(qc: ReturnType<typeof useQueryClient>, id?: string) {
  qc.invalidateQueries({ queryKey: ['admin', 'users'] });
  qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
  if (id) {
    qc.invalidateQueries({ queryKey: ['admin', 'users', 'detail', id] });
  }
}

export function useChangeUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: AdminUserRole }) =>
      changeAdminUserRole(id, role),
    onSuccess: (_data, vars) => invalidateUsers(qc, vars.id),
  });
}

export function useSuspendUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => suspendAdminUser(id, reason),
    onSuccess: (_data, vars) => invalidateUsers(qc, vars.id),
  });
}

export function useUnsuspendUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => unsuspendAdminUser(id),
    onSuccess: (_data, vars) => invalidateUsers(qc, vars.id),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteAdminUser(id),
    onSuccess: () => invalidateUsers(qc),
  });
}

export function useBulkUserAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { user_ids: string[]; action: BulkUserAction; reason?: string }) =>
      bulkAdminUserAction(input),
    onSuccess: () => invalidateUsers(qc),
  });
}

function invalidateClassrooms(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['admin', 'classrooms'] });
  qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
}

export function useAdminClassrooms(params: ListClassroomsParams) {
  return useQuery({
    queryKey: ['admin', 'classrooms', params],
    queryFn: () => listAdminClassrooms(params),
    placeholderData: (prev) => prev,
  });
}

export function usePauseClassroom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => pauseAdminClassroom(id),
    onSuccess: () => invalidateClassrooms(qc),
  });
}

export function useUnpauseClassroom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => unpauseAdminClassroom(id),
    onSuccess: () => invalidateClassrooms(qc),
  });
}

export function useSetClassroomAllowPublish() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, allow }: { id: string; allow: boolean }) =>
      setAdminClassroomAllowPublish(id, allow),
    onSuccess: () => invalidateClassrooms(qc),
  });
}

export function useDeleteClassroom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteAdminClassroom(id),
    onSuccess: () => invalidateClassrooms(qc),
  });
}

function invalidateSubmissions(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['admin', 'submissions'] });
  qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
}

export function useAdminSubmissions(params: ListSubmissionsParams) {
  return useQuery({
    queryKey: ['admin', 'submissions', params],
    queryFn: () => listAdminSubmissions(params),
    placeholderData: (prev) => prev,
  });
}

export function useForceApproveSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => forceApproveSubmission(id, note),
    onSuccess: () => invalidateSubmissions(qc),
  });
}

export function useForceRejectSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      forceRejectSubmission(id, reason),
    onSuccess: () => invalidateSubmissions(qc),
  });
}

export function useWithdrawSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => withdrawSubmission(id),
    onSuccess: () => invalidateSubmissions(qc),
  });
}

export function useAdminPublishedApps(params: ListPublishedAppsParams) {
  return useQuery({
    queryKey: ['admin', 'published-apps', params],
    queryFn: () => listAdminPublishedApps(params),
    placeholderData: (prev) => prev,
  });
}

export function useTakedownPublishedApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      takedownPublishedApp(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'published-apps'] });
      qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
}

function invalidateReports(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['admin', 'reports'] });
  qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
}

export function useAdminReports(params: ListReportsParams) {
  return useQuery({
    queryKey: ['admin', 'reports', params],
    queryFn: () => listAdminReports(params),
    placeholderData: (prev) => prev,
  });
}

export function useDismissReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => dismissReport(id, note),
    onSuccess: () => invalidateReports(qc),
  });
}

export function useActionReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => actionReport(id, note),
    onSuccess: () => invalidateReports(qc),
  });
}

export function useEmailOutbox() {
  return useQuery({
    queryKey: ['admin', 'email-outbox'],
    queryFn: () => getEmailOutbox(50),
    refetchInterval: 30_000,
  });
}

export function useSendDigests() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dryRun }: { dryRun: boolean }) => sendDigests(dryRun),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'email-outbox'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
    },
  });
}

export function useAdminHealth() {
  return useQuery({
    queryKey: ['admin', 'health'],
    queryFn: getAdminHealth,
    refetchInterval: 30_000,
  });
}

export function useFeatureFlags() {
  return useQuery({
    queryKey: ['admin', 'flags'],
    queryFn: listFeatureFlags,
  });
}

export function useCreateFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; enabled: boolean; description?: string }) =>
      createFeatureFlag(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'flags'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
    },
  });
}

export function useToggleFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      toggleFeatureFlag(id, enabled),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'flags'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
    },
  });
}

export function useAdminGlobalSearch(q: string, enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'search', q],
    queryFn: () => adminGlobalSearch(q),
    enabled,
  });
}

export function useAdminConfig() {
  return useQuery({
    queryKey: ['admin', 'config'],
    queryFn: getAdminConfig,
  });
}

export function useModerationAudit(filters: ModerationAuditFilters) {
  return useQuery({
    queryKey: ['admin', 'moderation-audit', filters],
    queryFn: () => listModerationAudit(filters),
    placeholderData: (prev) => prev,
  });
}

export function useAdminMessages(params: ListMessagesParams) {
  return useQuery({
    queryKey: ['admin', 'messages', params],
    queryFn: () => listAdminMessages(params),
    placeholderData: (prev) => prev,
    refetchInterval: 30_000,
  });
}

export function useHandleMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, note }: { id: string; status: InboundStatus; note?: string }) =>
      handleAdminMessage(id, status, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'messages'] });
      qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
}

export function useAdminAuditLog(filters: AdminAuditFilters) {
  return useQuery({
    queryKey: ['admin', 'audit-log', filters],
    queryFn: () => listAdminAuditLog(filters),
    placeholderData: (prev) => prev,
  });
}
